import { Dropdown, Avatar } from "antd";
import type { MenuProps } from "antd";
import { clsx } from "clsx";
import { ChevronLeftSquare, LogOut, ArrowLeft } from "lucide-react";
import { CaretDownFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  useAppDispatch,
  useAppSelector,
  clearCredentials,
  toggleSidebar,
} from "../../store";
import type { HeaderProps } from "../../types";

export function Header({
  onMobileMenuToggle,
  showBackButton = false,
  backButtonText = "Back",
  backRoute,
}: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { sidebarCollapsed } = useAppSelector((state) => state.settings);
  const { instance } = useMsal();

  const handleLogout = () => {
    // Clear Redux auth state
    dispatch(clearCredentials());

    // MSAL logout (clears MSAL cache and redirects to Azure logout)
    instance.logoutRedirect();
  };

  const accountMenuItems: MenuProps["items"] = [
    {
      key: "logout",
      label: "Logout",
      icon: <LogOut size={16} />,
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <header className="h-12.5 flex items-center justify-between px-2 bg-white border-b border-gray-200 py-9">
      <div className="flex items-center gap-4">
        {/* Mobile menu toggle */}
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded text-grey hover:bg-primary-light hover:text-primary duration-200"
          title="Toggle navigation menu"
        >
          <img src="/mini_logo.svg" alt="Logo" className="w-8" />
        </button>

        {/* Desktop sidebar toggle */}
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="hidden lg:flex p-2 rounded text-grey hover:bg-primary-light hover:text-primary duration-200"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeftSquare
            className={clsx(
              "transition-transform duration-200",
              sidebarCollapsed ? "rotate-180" : "",
            )}
            style={{ width: "20px", height: "auto" }}
          />
        </button>

        {/* Back Button - Conditional */}
        {showBackButton && backRoute && (
          <>
            <button
              onClick={() => navigate(backRoute)}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-grey hover:bg-primary-light hover:text-primary duration-200 transition-colors"
              title={backButtonText}
            >
              <ArrowLeft size={16} />
              <span className="text-sm font-medium">
                <span className="sm:hidden">
                  {backButtonText.length > 6
                    ? `${backButtonText.substring(0, 6)}...`
                    : backButtonText}
                </span>
                <span className="hidden sm:inline">{backButtonText}</span>
              </span>
            </button>
          </>
        )}
      </div>

      <div className="flex items-center gap-4">
        {/* Account Dropdown */}
        <Dropdown
          menu={{ items: accountMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button className="flex items-center gap-2 p-2 rounded hover:bg-primary-light transition-colors">
            <Avatar
              size="small"
              className="bg-primary text-white font-semibold"
            >
              {user?.name?.charAt(0).toUpperCase() || "A"}
            </Avatar>
            <span className="hidden sm:inline text-sm font-semibold text-customBlack">
              {user?.name || "Admin"}
            </span>
            <CaretDownFilled />
          </button>
        </Dropdown>
      </div>
    </header>
  );
}

export default Header;
